import { TimerHelper } from './../../../helpers/timer.helper';
import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, FormControl } from '@angular/forms';
import { SaveFormService } from '../../../services/save-form.service';
import { Subscription } from 'rxjs';
import { LocalizedName } from '../../../models/localized-name.model';
import { QuestionService } from '../../../services/question.service';
import { ConfigService } from '../../../services/config.service';
import { AdminQuestion, QuestionType, Answer } from '../../../models/questions';
import { AnswerCategoryType } from '../../../models/questions/answer-category-type.enum';
import { EnumDescription } from '../../../helpers';
import { NgxSelectOption } from '../../../../../node_modules/ngx-select-ex';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-checkbox-radio-form',
  templateUrl: './checkbox-radio-form.component.html',
  styles: [],
})
export class CheckboxRadioFormComponent implements OnInit, OnDestroy {
  @Input() question: AdminQuestion;
  @Input() questionType: QuestionType;
  @Input() categoryIds: Array<number> = [];

  emptyAnswer: Answer = { localizedNames: [{}, {}] };
  defaultAnswers: Array<Answer> = [this.emptyAnswer, this.emptyAnswer];
  questionRadiogroupCheckboxesForm: FormGroup;
  titlePropertiesArray: Array<string> = [];
  choicesPropertiesArray: Array<Array<string>> = [];
  ratesPropertiesArray: Array<Array<string>> = [];
  srPropertiesArray: Array<Array<string>> = [];
  partOfTitleControlName = 'title_';
  partOfCheckboxControlName = `choices`;
  partOfRatingControlName = `value_${this.partOfCheckboxControlName}`;
  partOfSatisfactionRateControlName = `sateisfactionRate_`;
  titlePlaceholderArray: Array<string> = [];
  choicesPlaceholderArray: Array<string> = [];
  colCount: number;
  answerCategoryTypes: Array<NgxSelectOption>;
  satisfactionRateFormControls: Array<FormControl> = [];

  private subscriptions: Subscription = new Subscription();
  private saveClicked: boolean;

  constructor(
    private questionFormBuilder: FormBuilder,
    private questionService: QuestionService,
    private saveFormService: SaveFormService,
    private appData: ConfigService,
    private readonly toastr: ToastrService
  ) {}

  ngOnInit() {
    if (!this.question) {
      this.question = {
        answerType: this.questionType,
        answers: this.defaultAnswers,
      };
    }
    this.createQuestionForm();
    this.questionRadiogroupCheckboxesForm.valueChanges.subscribe((x) => {
      this.saveFormService.formValueChanged(true);
      this.question.localizedNames = this.saveFormService.mapFormvalueToQuestion(
        this.questionRadiogroupCheckboxesForm.value,
        this.partOfTitleControlName
      );
    });
    this.subscriptions = this.saveFormService.saveButtonClicked$.subscribe(
      (x) => {
        if (x.question.id !== this.question.id) {
          return;
        }
        this.saveCheckboxForm(x.isAdd);
      }
    );
    this.subscriptions.add(this.addInputSubscription());
    this.question.answers = this.question.answers
      ? this.question.answers
      : this.defaultAnswers;
    this.question.categoryIds = this.categoryIds;
    this.buildFields();
    this.colCount = this.question.answers.length;
  }

  createQuestionForm() {
    this.questionRadiogroupCheckboxesForm = this.questionFormBuilder.group({});
  }

  addField() {
    this.colCount++;
    this.saveFormService.addInputToForm(
      this.questionRadiogroupCheckboxesForm,
      this.partOfCheckboxControlName,
      this.colCount
    );
    this.addValueToFormGroup(
      this.colCount,
      this.partOfCheckboxControlName,
      this.choicesPropertiesArray
    );
    if (this.getIsRating()) {
      this.addRateValueToFormGroup(this.colCount);
    }
    if (this.getIsSatisfactionRate()) {
      this.addSatisfactionValueToFormGroup(this.colCount);
    }
    this.setPlaceholders();
  }

  deleteInputGroup(item: Array<string>) {
    const index = this.choicesPropertiesArray.findIndex((i) => i === item);
    if (index === -1) {
      return;
    }
    this.choicesPropertiesArray.splice(index, 1);
    this.ratesPropertiesArray.splice(index, 1);
    item.forEach((x) => {
      this.questionRadiogroupCheckboxesForm.removeControl(x);
    });
  }

  getIsCheckbox(): boolean {
    return this.question.answerType === QuestionType.Checkbox;
  }

  getIsRadiobuttonValue(formControlName: string): boolean {
    return !formControlName.includes('value');
  }

  getIsRating(): boolean {
    return this.question.answerType === QuestionType.Rating;
  }

  getIsSatisfactionRate(): boolean {
    return this.question.answerType === QuestionType.SatisfactionRate;
  }

  canShowFormControlError(formControlName: string): boolean {
    return (
      this.saveClicked &&
      this.saveFormService.canShowFormControlFieldError(
        this.questionRadiogroupCheckboxesForm,
        formControlName
      )
    );
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  private mapAnswerCategoryTypesToNgxSelect() {
    const enumKeys = Object.keys(AnswerCategoryType).filter(
      (k) =>
        typeof AnswerCategoryType[k as any] === 'number' &&
        +AnswerCategoryType[k] !== 0
    );
    return enumKeys.map((x) =>
      Object.assign({
        id: AnswerCategoryType[x],
        text: EnumDescription.GetDescription(
          AnswerCategoryType,
          AnswerCategoryType[x]
        ),
      })
    );
  }

  private saveCheckboxForm(isAddForm: boolean) {
    this.saveClicked = true;
    TimerHelper.timer(
      this.appData.configData.displayTooltipTimeMilliseconds,
      () => (this.saveClicked = false)
    );
    if (this.questionRadiogroupCheckboxesForm.invalid) {
      return;
    }
    this.mapCheckboxQuestionFromForm();
    this.createUpdateQuestion(isAddForm);
  }

  private mapCheckboxQuestionFromForm() {
    let orderrates: Array<number> = [];
    let satisfactionRates: Array<number> = [];
    this.question.localizedNames = this.saveFormService.mapFormvalueToQuestion(
      this.questionRadiogroupCheckboxesForm.value,
      this.partOfTitleControlName
    );
    const answersLocalizedNames: Array<LocalizedName> = this.saveFormService.mapFormvalueToQuestion(
      this.questionRadiogroupCheckboxesForm.value,
      this.partOfCheckboxControlName
    );
    if (this.getIsRating()) {
      orderrates = this.saveFormService.mapFormvalueRateToQuestion(
        this.questionRadiogroupCheckboxesForm.value,
        this.partOfRatingControlName
      );
    }
    if (this.getIsSatisfactionRate()) {
      satisfactionRates = this.saveFormService.mapFormvalueRateToQuestion(
        this.questionRadiogroupCheckboxesForm.value,
        this.partOfSatisfactionRateControlName
      );
    }
    const joinedLocalizedNames = this.saveFormService.joinLocalizedNames(
      answersLocalizedNames
    );
    this.updateCheckboxAnswers(
      joinedLocalizedNames,
      orderrates,
      satisfactionRates
    );
  }

  private createUpdateQuestion(isCreate: boolean) {
    if (isCreate) {
      this.questionService.create(this.question).subscribe((x) => {
        this.saveFormService.closeForm(this.question);
      });
    } else {
      this.questionService.update(this.question).subscribe(
        (x) => {
          this.saveFormService.closeForm(this.question);
        },
        (err) => {
          this.toastr.error(err.error, 'Error');
        }
      );
    }
  }

  private updateCheckboxAnswers(
    answersLocalizedNames: Array<Array<LocalizedName>>,
    orders: Array<number> = [],
    satisfactionRates: Array<number> = []
  ) {
    this.question.answers.forEach((x, i) => {
      x.localizedNames = answersLocalizedNames[i];
      x.order = orders.length > 0 ? orders[i] : x.order;
    });
    if (this.question.answers.length < answersLocalizedNames.length) {
      this.addAnswer(
        this.question.answers.length,
        answersLocalizedNames,
        orders,
        satisfactionRates
      );
    } else if (this.question.answers.length > answersLocalizedNames.length) {
      this.removeAnswers(answersLocalizedNames.length);
    }
  }

  private addAnswer(
    startIndex,
    answersLocalizedNames,
    orders,
    satisfactionRates
  ) {
    if (this.question.answerType === QuestionType.SatisfactionRate) {
      this.addSatisfactionRateAnswer(
        startIndex,
        answersLocalizedNames,
        satisfactionRates
      );
    } else if (this.question.answerType === QuestionType.Rating) {
      this.addRatingAnswer(startIndex, answersLocalizedNames, orders);
    } else {
      this.addCheckboxAnswer(
        this.question.answers.length,
        answersLocalizedNames
      );
    }
  }

  private addSatisfactionRateAnswer(
    startIndex: number,
    items: Array<Array<LocalizedName>>,
    satisfactionRate: Array<number>
  ) {
    for (let index = startIndex; index < items.length; index++) {
      this.question.answers.push({
        order:
          this.question.answers.length > 0
            ? this.question.answers[index - 1].order + 1
            : 1,
        localizedNames: items[index],
      });
    }
    this.question.answers.forEach(
      (q, i) => (q.answerCategoryType = satisfactionRate[i])
    );
  }

  private addCheckboxAnswer(
    startIndex: number,
    items: Array<Array<LocalizedName>>
  ) {
    for (let index = startIndex; index < items.length; index++) {
      this.question.answers.push({
        order:
          this.question.answers.length > 0
            ? this.question.answers[index - 1].order + 1
            : 1,
        localizedNames: items[index],
      });
    }
  }

  private addRatingAnswer(
    startIndex: number,
    items: Array<Array<LocalizedName>>,
    orders: Array<number>
  ) {
    for (let index = startIndex; index < items.length; index++) {
      this.question.answers.push({
        localizedNames: items[index],
      });
    }
    this.question.answers.forEach((q, i) => (q.order = orders[i]));
  }

  private removeAnswers(startIndex: number) {
    for (
      let index = startIndex;
      index < this.question.answers.length;
      index++
    ) {
      this.question.answers.splice(index, 1);
    }
  }

  private addInputSubscription() {
    this.saveFormService.addInputButtonClicked$.subscribe((s) => {
      if (s.isColumn) {
        this.addField();
      }
    });
  }

  private buildFields() {
    this.answerCategoryTypes = this.mapAnswerCategoryTypesToNgxSelect();
    this.saveFormService.addAllLocalizedNameToFormGroup(
      this.questionRadiogroupCheckboxesForm,
      this.partOfTitleControlName,
      this.question.localizedNames
    );
    this.titlePropertiesArray = this.saveFormService.mapFormGroupControlToPropArray(
      this.questionRadiogroupCheckboxesForm,
      this.partOfTitleControlName
    );
    this.saveFormService.addValidationToLocalizedControls(
      this.questionRadiogroupCheckboxesForm,
      this.partOfTitleControlName
    );
    for (let index = 0; index < this.question.answers.length; index++) {
      this.addValueToFormGroup(
        index,
        this.partOfCheckboxControlName,
        this.choicesPropertiesArray
      );
      this.addRateValueToFormGroup(index);
      this.addSatisfactionValueToFormGroup(index);
    }
    this.setPlaceholders();
  }

  private addSatisfactionValueToFormGroup(index: number) {
    if (this.question.answerType !== QuestionType.SatisfactionRate) {
      return;
    }
    this.addSatisfactionRateValues(
      this.questionRadiogroupCheckboxesForm,
      `${this.partOfSatisfactionRateControlName}_${index}`,
      this.question.answers[index]
    );
    const choicesLangArray: Array<string> = this.saveFormService.mapFormGroupControlToPropArray(
      this.questionRadiogroupCheckboxesForm,
      `${this.partOfSatisfactionRateControlName}_${index}`
    );
    this.saveFormService.addValidationToNumberTypeControls(
      this.questionRadiogroupCheckboxesForm,
      this.partOfSatisfactionRateControlName
    );
    this.srPropertiesArray.push(choicesLangArray);
  }

  private addSatisfactionRateValues(
    questionForm: FormGroup,
    partOfControlName: string,
    answer: Answer = {}
  ) {
    const formControl = answer.id
      ? new FormControl(answer.answerCategoryType)
      : new FormControl('');
    questionForm.addControl(`${partOfControlName}`, formControl);
  }

  private addRateValueToFormGroup(index: number) {
    if (this.question.answerType !== QuestionType.Rating) {
      return;
    }
    this.saveFormService.addRatingValueToFormGroup(
      this.questionRadiogroupCheckboxesForm,
      `${this.partOfRatingControlName}_${index}`,
      this.question.answers[index]
    );
    const choicesLangArray: Array<string> = this.saveFormService.mapFormGroupControlToPropArray(
      this.questionRadiogroupCheckboxesForm,
      `${this.partOfRatingControlName}_${index}`
    );
    this.saveFormService.addValidationToNumberTypeControls(
      this.questionRadiogroupCheckboxesForm,
      this.partOfRatingControlName
    );
    this.ratesPropertiesArray.push(choicesLangArray);
  }

  private addValueToFormGroup(
    index: number,
    partOfControlName: string,
    propertiesArray: Array<Array<string>>
  ) {
    const localizedNames = this.question.answers[index]
      ? this.question.answers[index].localizedNames
      : [];
    this.saveFormService.addAllLocalizedNameToFormGroup(
      this.questionRadiogroupCheckboxesForm,
      `${partOfControlName}_${index}_`,
      localizedNames
    );
    const choicesLangArray: Array<string> = this.saveFormService.mapFormGroupControlToPropArray(
      this.questionRadiogroupCheckboxesForm,
      `${partOfControlName}_${index}_`
    );
    this.saveFormService.addValidationToLocalizedControls(
      this.questionRadiogroupCheckboxesForm,
      partOfControlName
    );
    propertiesArray.push(choicesLangArray);
  }

  private setPlaceholders() {
    const allControls = this.saveFormService.getAllFormControlsFields(
      this.questionRadiogroupCheckboxesForm.controls
    );
    const titleControls = this.saveFormService.getControlsByControlName(
      this.partOfTitleControlName,
      allControls
    );
    const choicesControls = this.saveFormService.getControlsByControlName(
      this.partOfCheckboxControlName,
      allControls
    );
    this.titlePlaceholderArray = this.saveFormService.setPlaceholderArrayByPropName(
      titleControls
    );
    this.choicesPlaceholderArray = this.saveFormService.setPlaceholderArrayByPropName(
      choicesControls
    );
  }
}
